import type { CSSProperties } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import { useNetwork } from '../hooks/useNetwork';
import { useNotes } from '../hooks/useNotes';
import { useCategories } from '../hooks/useCategories';
import { useReminders } from '../hooks/useReminders';
import { RecordButton } from '../components/RecordButton';
import { OfflineBanner } from '../components/OfflineBanner';
import { NotesList } from '../components/NotesList';
import { CategoryGrid } from '../components/CategoryGrid';
import { TypeFilterTabs } from '../components/TypeFilterTabs';
import { createNote, updateNote } from '../services/api';
import { useToast } from '../hooks/useToast';
import { Toast } from '../components/Toast';

interface SavedNote {
  noteId: string;
  storagePath: string;
  durationMs: number;
  intent?: string | null;
}

function intentLabel(intent: string): string {
  switch (intent) {
    case 'reminder': return 'Reminder detected';
    case 'task': return 'Task detected';
    default: return 'Note';
  }
}

export function HomePage() {
  const { user, signOut } = useAuth();
  const { online } = useNetwork();
  const { notes, loading, fromCache, refresh } = useNotes();
  const { categories, refresh: refreshCategories } = useCategories();
  const { reminders } = useReminders();
  const { toast, showError, dismiss } = useToast();
  const navigate = useNavigate();
  const [lastNote, setLastNote] = useState<SavedNote | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string | null>(null);

  const handleRecorded = useCallback(
    async (data: SavedNote) => {
      setLastNote({ ...data, intent: null });
      try {
        const note = await createNote({
          id: data.noteId,
          audio_url: data.storagePath,
          duration_seconds: Math.round(data.durationMs / 1000),
        });
        setLastNote({ ...data, intent: note.intent });
      } catch {
        showError('Failed to create note');
      }
      refresh();
      refreshCategories();
    },
    [refresh, refreshCategories, showError]
  );

  const handleTogglePin = useCallback(
    async (noteId: string, pinned: boolean) => {
      try {
        await updateNote(noteId, { pinned });
        refresh();
      } catch {
        showError('Failed to toggle pin');
      }
    },
    [refresh, showError]
  );

  const pendingReminders = useMemo(
    () => reminders.filter((r) => !r.triggered),
    [reminders]
  );

  const filteredNotes = useMemo(() => {
    let result = notes;
    if (selectedCategoryId) {
      result = result.filter((n) => n.category_id === selectedCategoryId);
    }
    if (selectedTypeFilter) {
      result = result.filter((n) =>
        selectedTypeFilter === 'note'
          ? n.intent === 'note' || !n.intent
          : n.intent === selectedTypeFilter
      );
    }
    return result;
  }, [notes, selectedCategoryId, selectedTypeFilter]);

  const typeCounts = useMemo(() => ({
    all: notes.length,
    note: notes.filter((n) => n.intent === 'note' || !n.intent).length,
    task: notes.filter((n) => n.intent === 'task').length,
    reminder: notes.filter((n) => n.intent === 'reminder').length,
  }), [notes]);

  return (
    <div style={styles.container}>
      {/* Top bar */}
      <div style={styles.topBar}>
        {user?.user_metadata?.avatar_url && (
          <img
            src={user.user_metadata.avatar_url as string}
            alt=""
            style={styles.avatar}
            referrerPolicy="no-referrer"
          />
        )}
        <span style={styles.greeting}>
          {(user?.user_metadata?.full_name as string)?.split(' ')[0] || 'Voice'}
        </span>
        <button style={styles.signOutBtn} onClick={signOut} type="button">
          Sign out
        </button>
      </div>

      {/* Offline banner */}
      {!online && <OfflineBanner />}

      {/* Record area */}
      <div style={styles.recordArea}>
        {online ? (
          <RecordButton onRecorded={handleRecorded} />
        ) : (
          <div style={styles.offlineRecordPlaceholder}>
            <div style={styles.disabledCircle}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity={0.3}>
                <rect x="9" y="1" width="6" height="12" rx="3" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </div>
            <p style={styles.offlineHint}>Recording unavailable offline</p>
          </div>
        )}
      </div>

      {/* Saved confirmation */}
      {lastNote && online && (
        <div style={styles.savedBanner}>
          <p style={styles.savedText}>
            Saved ({Math.round(lastNote.durationMs / 1000)}s)
            {lastNote.intent && (
              <span style={styles.savedIntent}>
                {' \u00B7 '}{intentLabel(lastNote.intent)}
              </span>
            )}
          </p>
        </div>
      )}

      {/* Category grid */}
      {categories.length > 0 && (
        <CategoryGrid
          categories={categories}
          selectedId={selectedCategoryId}
          onSelect={setSelectedCategoryId}
          onRefresh={refreshCategories}
        />
      )}

      {/* Type filter tabs */}
      <TypeFilterTabs
        selected={selectedTypeFilter}
        onSelect={setSelectedTypeFilter}
        counts={typeCounts}
      />

      {/* Upcoming reminders */}
      {pendingReminders.length > 0 && (
        <div style={styles.remindersSection}>
          <span style={styles.remindersSectionTitle}>Upcoming Reminders</span>
          {pendingReminders.slice(0, 3).map((r) => (
            <div
              key={r.id}
              style={styles.reminderCard}
              onClick={() => navigate(`/notes/${r.note_id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/notes/${r.note_id}`); }}
            >
              <span style={styles.reminderIcon}>&#128276;</span>
              <span style={styles.reminderCardDate}>
                {new Date(r.remind_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              <span style={styles.reminderCardNote}>
                {r.notes.summary ?? r.notes.title ?? 'Untitled'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Notes timeline */}
      <div style={styles.notesSection}>
        {loading ? (
          <div style={styles.skeletonList}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={styles.skeletonCard}>
                <div style={styles.skeletonLine} />
                <div style={{ ...styles.skeletonLine, width: '60%' }} />
              </div>
            ))}
          </div>
        ) : (
          <NotesList
            notes={filteredNotes}
            fromCache={fromCache}
            onTogglePin={handleTogglePin}
            categories={categories}
          />
        )}
      </div>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismiss} />}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    padding: 'var(--space-4)',
    gap: 'var(--space-4)',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-full)',
  },
  greeting: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    flex: 1,
  },
  signOutBtn: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    padding: '4px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
  },
  recordArea: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 'var(--space-2)',
    paddingBottom: 'var(--space-2)',
  },
  offlineRecordPlaceholder: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 'var(--space-3)',
  },
  disabledCircle: {
    width: '68px',
    height: '68px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-surface)',
    border: '2px dashed var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  offlineHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
  },
  savedBanner: {
    textAlign: 'center' as const,
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'var(--color-success-light)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(34, 197, 94, 0.2)',
  },
  savedText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-success)',
    fontWeight: 500,
  },
  savedIntent: {
    color: 'var(--color-accent)',
    fontWeight: 600,
  },
  notesSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  skeletonList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
  },
  skeletonCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
    padding: 'var(--space-3)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
  },
  skeletonLine: {
    height: '12px',
    borderRadius: 'var(--radius-sm)',
    background: 'linear-gradient(90deg, var(--color-border) 25%, var(--color-surface-elevated) 50%, var(--color-border) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s ease-in-out infinite',
  } as CSSProperties,
  remindersSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
  },
  remindersSectionTitle: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  reminderCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'var(--color-warning-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  reminderIcon: {
    fontSize: 'var(--font-size-sm)',
  },
  reminderCardDate: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-warning)',
  },
  reminderCardNote: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    flex: 1,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  },
};
